import { formatDate } from "@/lib/utils";
import type { FleetUnit } from "@/api/fleet";

const colorForEstado: Record<string, string> = {
  patrullando: "#10B981",
  en_operativo: "#8B5CF6",
  hit_activo: "#BE123C",
  detenido: "#F59E0B",
  en_pausa: "#F59E0B",
  offline: "#6B7280",
};

interface Props {
  units: FleetUnit[];
  selectedUnitId: number | null;
  onSelect: (unit: FleetUnit) => void;
}

export function UnitsSidebar({ units, selectedUnitId, onSelect }: Props) {
  return (
    <aside
      className="w-80 shrink-0 border-r border-border bg-surface overflow-y-auto"
      data-testid="fleet-sidebar"
    >
      <header className="px-4 py-3 border-b border-border">
        <h2 className="text-sm font-semibold">Flota</h2>
        <p className="text-xs text-muted-200">
          {units.length} unidad(es)
        </p>
      </header>
      <ul>
        {units.map((u) => (
          <UnitRow
            key={u.unitId}
            unit={u}
            selected={u.unitId === selectedUnitId}
            onClick={() => onSelect(u)}
          />
        ))}
      </ul>
    </aside>
  );
}

function UnitRow({
  unit,
  selected,
  onClick,
}: {
  unit: FleetUnit;
  selected: boolean;
  onClick: () => void;
}) {
  const kpi = unit.kpiToday;
  const hits = kpi?.hits ?? 0;
  const km = kpi?.distanciaKm ?? 0;
  return (
    <li
      className={`px-4 py-2 border-b border-border/50 cursor-pointer transition-colors ${
        selected ? "bg-surface2" : "hover:bg-surface2/50"
      }`}
      data-testid={`fleet-unit-${unit.unitId}`}
      onClick={onClick}
    >
      <div className="flex items-center gap-2">
        <span
          className="h-2 w-2 rounded-full"
          style={{ background: colorForEstado[unit.estado] ?? "#6B7280" }}
        />
        <span className="text-sm font-medium">{unit.alias}</span>
        <span className="ml-auto text-[10px] uppercase text-muted-200">
          {unit.estado}
        </span>
      </div>
      <div className="text-xs text-muted-200 mt-0.5">
        {unit.operadorNombre ?? "Sin operador"} · {unit.placa ?? "—"}
      </div>
      <div className="flex items-center gap-3 mt-1 text-[10px] text-muted-200">
        <span>
          Latido {formatDate(unit.lastHeartbeatAt)}
        </span>
        {hits > 0 && (
          <span className="text-rose-400 font-medium">{hits} hit(s) hoy</span>
        )}
        {km > 0 && <span>{km.toFixed(1)} km</span>}
        {unit.pausaActiva && (
          <span className="text-amber-400">pausada</span>
        )}
      </div>
    </li>
  );
}
